import {
    Constants,
    RuleData,
    Vehicle,
    VehicleComponent,
    VehicleComponentUtils,
    VehicleUtils,
} from '@dndbeyond/character-rules-engine/es';

import {
    deriveEnableComponentManagement,
    deriveFeaturesProps,
    deriveVehicleActionStationsProps,
    deriveVehicleBlockActionsActions,
    deriveVehicleBlockActionsSummariesProps,
    deriveVehicleBlockComponentHitPointInfoProps,
    deriveVehicleBlockPrimaryHitPointInfoProps,
    deriveVehicleComponentActionsProps,
    deriveVehicleComponentArmorClassInfo,
    deriveVehicleComponentCargoCapacityProp,
    deriveVehicleComponentCoverTypeProp,
    deriveVehicleComponentPrimaryPropertiesProps,
    deriveVehicleComponentRequiredCrew,
    deriveVehicleComponentSpeedInfos,
    deriveVehicleComponentsProps,
    deriveVehicleComponentTravelPaceInfoProp,
    deriveVehicleConditionImmunitiesProps,
    deriveVehicleDamageImmunitiesProps,
    deriveVehicleSizeNameProp,
    deriveVehicleTypeNameProp,
} from './derivers';
import {
    GD_VehicleBlockActionsProps,
    GD_VehicleBlockActionStationListProps,
    GD_VehicleBlockActionStationProps,
    GD_VehicleBlockActionSummariesProps,
    GD_VehicleBlockComponentListProps,
    GD_VehicleBlockComponentProps,
    GD_VehicleBlockFeaturesProps,
    GD_VehicleBlockHeaderProps,
    GD_VehicleBlockPrimaryComponentProps,
    GD_VehicleBlockPrimaryProps,
    GD_VehicleBlockProps,
    GD_VehicleBlockShellProps,
} from './typings';

/**
 *
 * @param vehicle
 * @param ruleData
 */
export function generateVehicleBlockProps(vehicle: Vehicle, ruleData: RuleData): GD_VehicleBlockProps {
    return {
        shellProps: generateVehicleShellProps(vehicle),
        headerProps: generateVehicleBlockHeaderProps(vehicle, ruleData),
        primaryProps: generateVehicleBlockPrimaryProps(vehicle),
        actionSummariesProps: generateVehicleBlockActionSummariesProps(vehicle, ruleData),
        featuresProps: generateVehicleFeaturesProps(vehicle),
        actionStationsProps: generateVehicleBlockActionStationListProps(vehicle, ruleData),
        componentsProps: generateVehicleBlockComponentListProps(vehicle, ruleData),
        actionsProps: generateVehicleBlockActionsProps(vehicle),
    };
}

/**
 *
 * @param vehicle
 */
export function generateVehicleShellProps(vehicle: Vehicle): GD_VehicleBlockShellProps {
    return {
        displayType: VehicleUtils.getConfigurationValue(Constants.VehicleConfigurationKeyEnum.DISPLAY_TYPE, vehicle),
    };
}

/**
 *
 * @param vehicle
 * @param ruleData
 */
export function generateVehicleBlockHeaderProps(vehicle: Vehicle, ruleData: RuleData): GD_VehicleBlockHeaderProps {
    return {
        name: VehicleUtils.getName(vehicle),
        typeName: deriveVehicleTypeNameProp(vehicle, ruleData),
        weight: VehicleUtils.getWeight(vehicle),
        length: VehicleUtils.getLength(vehicle),
        width: VehicleUtils.getWidth(vehicle),
        sizeName: deriveVehicleSizeNameProp(vehicle),
    };
}

/**
 *
 * @param vehicle
 */
export function generateVehicleBlockPrimaryProps(vehicle: Vehicle): GD_VehicleBlockPrimaryProps {
    return {
        cargoCapacityInfo: deriveVehicleComponentCargoCapacityProp(vehicle),
        conditionImmunities: deriveVehicleConditionImmunitiesProps(vehicle),
        creatureCapacityDescriptions: VehicleUtils.getCreatureCapacityDescriptions(vehicle),
        damageImmunities: deriveVehicleDamageImmunitiesProps(vehicle),
        displayType: VehicleUtils.getConfigurationValue(Constants.VehicleConfigurationKeyEnum.DISPLAY_TYPE, vehicle),
        primaryProperties: deriveVehicleComponentPrimaryPropertiesProps(vehicle),
        stats: VehicleUtils.getStats(vehicle),
        travelPaceInfo: deriveVehicleComponentTravelPaceInfoProp(vehicle),
    };
}

/**
 *
 * @param vehicle
 * @param ruleData
 */
export function generateVehicleBlockActionSummariesProps(
    vehicle: Vehicle,
    ruleData: RuleData,
): GD_VehicleBlockActionSummariesProps {
    return {
        actionsText: VehicleUtils.getActionsText(vehicle),
        actionsSummaries: deriveVehicleBlockActionsSummariesProps(vehicle, ruleData),
    };
}

/**
 *
 * @param vehicle
 */
export function generateVehicleFeaturesProps(vehicle: Vehicle): GD_VehicleBlockFeaturesProps {
    return {
        features: deriveFeaturesProps(vehicle),
    };
}

/**
 *
 * @param vehicle
 * @param ruleData
 */
export function generateVehicleBlockActionStationListProps(
    vehicle: Vehicle,
    ruleData: RuleData,
): GD_VehicleBlockActionStationListProps {
    const enableActionStations = VehicleUtils.getConfigurationValue(
        Constants.VehicleConfigurationKeyEnum.ENABLE_ACTION_STATIONS,
        vehicle,
    );

    return {
        actionStations: enableActionStations ? deriveVehicleActionStationsProps(vehicle, ruleData) : [],
    };
}

/**
 *
 * @param station
 * @param vehicle
 * @param ruleData
 */
export function generateVehicleActionStationProps(
    station: VehicleComponent,
    vehicle: Vehicle,
    ruleData: RuleData,
): GD_VehicleBlockActionStationProps {
    //share componentProps for now.
    return generateVehicleBlockComponentProps(station, vehicle, ruleData);
}

/**
 *
 * @param vehicle
 */
export function generateVehiclePrimaryComponentProps(vehicle: Vehicle): GD_VehicleBlockPrimaryComponentProps | null {
    const primaryComponent = VehicleUtils.getPrimaryComponent(vehicle);

    if (primaryComponent === null) {
        return null;
    }

    return {
        armorClassInfo: VehicleComponentUtils.getArmorClassInfo(primaryComponent),
        hitPointInfo: deriveVehicleBlockPrimaryHitPointInfoProps(vehicle),
        speedInfos: VehicleComponentUtils.getSpeedInfos(primaryComponent),
    };
}

/**
 *
 * @param vehicle
 */
export function generateVehicleBlockActionsProps(vehicle: Vehicle): GD_VehicleBlockActionsProps {
    const allActions = VehicleUtils.getAllActions(vehicle);

    return {
        reactions: deriveVehicleBlockActionsActions(Constants.ActivationTypeEnum.REACTION, allActions),
        bonusActions: deriveVehicleBlockActionsActions(Constants.ActivationTypeEnum.BONUS_ACTION, allActions),
        special: deriveVehicleBlockActionsActions(Constants.ActivationTypeEnum.SPECIAL, allActions),
    };
}

/**
 *
 * @param vehicle
 * @param ruleData
 */
export function generateVehicleBlockComponentListProps(
    vehicle: Vehicle,
    ruleData: RuleData,
): GD_VehicleBlockComponentListProps {
    const enableComponents = VehicleUtils.getConfigurationValue(
        Constants.VehicleConfigurationKeyEnum.ENABLE_COMPONENTS,
        vehicle,
    );

    return {
        components: enableComponents ? deriveVehicleComponentsProps(vehicle, ruleData) : [],
    };
}

/**
 *
 * @param component
 * @param vehicle
 * @param ruleData
 */
export function generateVehicleBlockComponentProps(
    component: VehicleComponent,
    vehicle: Vehicle,
    ruleData: RuleData,
): GD_VehicleBlockComponentProps {
    const uniqueKey = VehicleComponentUtils.getUniqueKey(component);
    return {
        actions: deriveVehicleComponentActionsProps(component),
        count: 1,
        description: VehicleComponentUtils.getDescription(component),
        displayOrder: VehicleComponentUtils.getDisplayOrder(component),
        isPrimaryComponent: VehicleComponentUtils.getIsPrimary(component),
        isRemovable: VehicleComponentUtils.getIsRemovable(component),
        name: VehicleComponentUtils.getName(component),
        typeNames: VehicleComponentUtils.getTypeNames(component),
        coverType: deriveVehicleComponentCoverTypeProp(component, vehicle, ruleData),
        requiredCrew: deriveVehicleComponentRequiredCrew(component, vehicle),
        key: uniqueKey,
        uniqueKey,
        uniquenessFactor: VehicleComponentUtils.getUniquenessFactor(component),
        id: VehicleComponentUtils.getMappingId(component),
        vehicleId: VehicleUtils.getMappingId(vehicle),

        armorClassInfo: deriveVehicleComponentArmorClassInfo(component, vehicle),
        speedInfos: deriveVehicleComponentSpeedInfos(component, vehicle),
        hitPointInfo: deriveVehicleBlockComponentHitPointInfoProps(component, vehicle),
        enableComponentManagement: deriveEnableComponentManagement(component, vehicle),
    };
}
